/**
 * LoginJournalTab — Journal des connexions du personnel
 * Qui se connecte via SSO Microsoft, qui utilise encore un mot de passe local, depuis quand.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, KeyRound, ShieldCheck, UserX, Inbox } from 'lucide-react';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_META = {
  microsoft: { label: 'Microsoft SSO', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
  local: { label: 'Mot de passe', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  mixte: { label: 'Mixte', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  aucune: { label: 'Aucune connexion', cls: 'bg-gray-50 text-gray-500 border-gray-200' },
  bloque: { label: 'Refusé (SSO requis)', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const Pill = ({ method }) => {
  const m = METHOD_META[method] || METHOD_META.aucune;
  return <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full border ${m.cls}`}>{m.label}</span>;
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const Kpi = ({ icon: Icon, title, value, subtext, accent, testid }) => (
  <div data-testid={testid} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
    <div className="flex items-start justify-between">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{title}</span>
      <Icon className={`w-4 h-4 ${accent}`} />
    </div>
    <div className="text-2xl font-bold mt-2 text-gray-900">{value}</div>
    <div className="text-xs text-gray-500 mt-1">{subtext}</div>
  </div>
);

const LoginJournalTab = () => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [data, setData] = useState({ summary: {}, users: [], recent: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/logs/login-journal?days=${days}`, {});
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const s = data.summary || {};

  return (
    <div className="space-y-5" data-testid="login-journal-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Journal des connexions du personnel</h2>
          <p className="text-sm text-gray-600">
            Méthode d'authentification réellement utilisée par chaque membre du staff
            {s.policyEnforced ? ' — politique SSO Microsoft active.' : ' — politique SSO Microsoft désactivée.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[150px] bg-white" data-testid="login-journal-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} data-testid="login-journal-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />Actualiser
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {loading && !data.users.length ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
        ) : (
          <>
            <Kpi testid="kpi-sso-only" icon={ShieldCheck} title="100 % Microsoft" value={s.ssoOnly || 0} subtext={`sur ${s.staffTotal || 0} comptes staff`} accent="text-blue-500" />
            <Kpi testid="kpi-local-used" icon={KeyRound} title="Encore au mot de passe" value={s.localUsed || 0} subtext={`${s.exceptions || 0} exception(s) autorisée(s)`} accent="text-amber-500" />
            <Kpi testid="kpi-no-login" icon={UserX} title="Aucune connexion" value={s.noLogin || 0} subtext={`sur la période (${s.days || 0} j)`} accent="text-gray-400" />
            <Kpi testid="kpi-blocked" icon={ShieldCheck} title="Refus SSO" value={s.blocked || 0} subtext="tentatives mot de passe bloquées" accent="text-red-500" />
          </>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Par utilisateur</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Utilisateur', 'Rôle', 'Méthode utilisée', 'Compte Microsoft lié', 'SSO depuis', 'Dernière connexion', 'Connexions (SSO / MDP)'].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-100"><td colSpan={7} className="px-4 py-3"><Skeleton className="h-8 w-full" /></td></tr>
                ))
              ) : data.users.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-14 text-center" data-testid="login-journal-empty">
                  <Inbox className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">Aucun compte staff trouvé</p>
                </td></tr>
              ) : data.users.map((u) => (
                <tr key={u.email} className="border-b border-gray-100 hover:bg-gray-50/70" data-testid={`login-journal-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">{u.name}</div>
                    <div className="text-xs text-gray-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{u.role}</td>
                  <td className="px-4 py-3">
                    <Pill method={u.method} />
                    {u.localLoginAllowed && <div className="text-xs text-amber-700 mt-1">Exception locale</div>}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {u.hasMicrosoftLink
                      ? <span className="text-emerald-700">Oui</span>
                      : <span className="text-gray-400">Non</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{fmt(u.firstSsoAt)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{fmt(u.lastSsoAt || u.lastLocalAt || u.lastLoginAt)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{u.ssoCount} / {u.localCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-200 text-sm font-medium text-gray-700">Dernières connexions</div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Date / Heure', 'Utilisateur', 'Méthode', 'IP'].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.recent || []).length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500" data-testid="login-journal-recent-empty">Aucun évènement de connexion sur la période</td></tr>
              ) : data.recent.map((e, i) => (
                <tr key={i} className="border-b border-gray-100" data-testid="login-journal-event-row">
                  <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{fmt(e.timestamp)}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-900">{e.email}</td>
                  <td className="px-4 py-2.5"><Pill method={e.method} /></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-gray-500">{e.ip || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LoginJournalTab;
